import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Phone, Mail, PartyPopper, Star, Sparkles, Sun } from "lucide-react";
import { SITE } from "../data/site";

const MotionLink = motion(Link);

export const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.55, delay, ease: "easeOut" }}
  >
    {children}
  </motion.div>
);

export const SectionHeading = ({ eyebrow, title, sub, testid }) => (
  <div className="mx-auto mb-12 max-w-2xl text-center" data-testid={testid}>
    {eyebrow && (
      <span className="mb-4 inline-block rounded-full bg-blue-100 px-4 py-1.5 text-xs font-extrabold uppercase tracking-widest text-blue-700">
        {eyebrow}
      </span>
    )}
    <h2 className="text-3xl font-semibold sm:text-4xl lg:text-5xl">{title}</h2>
    {sub && <p className="mt-4 text-base text-slate-600 md:text-lg">{sub}</p>}
  </div>
);

export const CallButton = ({ testid = "call-button", className = "", children, light = false }) => (
  <motion.a
    href={SITE.phoneHref}
    data-testid={testid}
    whileHover={{ y: -3, scale: 1.04 }}
    whileTap={{ scale: 0.96 }}
    transition={{ type: "spring", stiffness: 400, damping: 17 }}
    className={`inline-flex items-center justify-center gap-2 rounded-full px-7 py-3.5 text-base font-extrabold shadow-lg focus:outline-none focus:ring-4 focus:ring-red-300 ${
      light
        ? "bg-white text-red-600 shadow-black/10"
        : "bg-red-500 text-white shadow-red-500/30"
    } ${className}`}
  >
    <Phone className="h-5 w-5" aria-hidden="true" />
    {children || "Call Ms. Jeanette"}
  </motion.a>
);

export const PartyButton = ({ testid = "party-button", className = "", children }) => (
  <MotionLink
    to="/birthday-parties"
    data-testid={testid}
    whileHover={{ y: -3, scale: 1.04 }}
    whileTap={{ scale: 0.96 }}
    transition={{ type: "spring", stiffness: 400, damping: 17 }}
    className={`inline-flex items-center justify-center gap-2 rounded-full bg-yellow-400 px-7 py-3.5 text-base font-extrabold text-slate-900 shadow-lg shadow-yellow-400/40 focus:outline-none focus:ring-4 focus:ring-yellow-300 ${className}`}
  >
    <PartyPopper className="h-5 w-5" aria-hidden="true" />
    {children || "Plan a Birthday Party"}
  </MotionLink>
);

export const EmailButton = ({ testid = "email-button", className = "", children }) => (
  <motion.a
    href={SITE.emailHref}
    data-testid={testid}
    whileHover={{ y: -3, scale: 1.04 }}
    whileTap={{ scale: 0.96 }}
    transition={{ type: "spring", stiffness: 400, damping: 17 }}
    className={`inline-flex items-center justify-center gap-2 rounded-full border-2 border-blue-500 bg-white px-7 py-3.5 text-base font-extrabold text-blue-600 shadow-lg shadow-blue-500/10 focus:outline-none focus:ring-4 focus:ring-blue-300 ${className}`}
  >
    <Mail className="h-5 w-5" aria-hidden="true" />
    {children || "Email Ms. Jeanette"}
  </motion.a>
);

export const Confetti = () => (
  <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
    <Star className="absolute left-[6%] top-[18%] h-5 w-5 text-yellow-400/70" />
    <Sparkles className="absolute right-[10%] top-[12%] h-6 w-6 text-red-400/50" />
    <Sun className="absolute bottom-[16%] left-[12%] h-7 w-7 text-yellow-500/40" />
    <Star className="absolute bottom-[24%] right-[7%] h-4 w-4 text-blue-400/60" />
    <Sparkles className="absolute left-[45%] top-[8%] h-4 w-4 text-yellow-500/50" />
  </div>
);

export const PageHero = ({ eyebrow, title, sub, testid, children, bg = "bg-blue-50" }) => (
  <section className={`relative overflow-hidden ${bg} px-4 pb-16 pt-14 sm:px-6 sm:pb-20 sm:pt-16`} data-testid={testid}>
    <Confetti />
    <div className="relative mx-auto max-w-3xl text-center">
      {eyebrow && (
        <span className="mb-4 inline-block rounded-full bg-white px-4 py-1.5 text-xs font-extrabold uppercase tracking-widest text-blue-700 shadow-sm">
          {eyebrow}
        </span>
      )}
      <h1 className="text-4xl font-semibold sm:text-5xl lg:text-6xl">{title}</h1>
      {sub && <p className="mx-auto mt-5 max-w-2xl text-base text-slate-600 md:text-lg">{sub}</p>}
      {children && <div className="mt-8 flex flex-wrap items-center justify-center gap-4">{children}</div>}
    </div>
  </section>
);
